import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import { SERVICE_REGIONS } from '../supplier-portal/regions.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Tedarikçi (ADR 0026 §1; `satin-alma-modulu.md` §4 "Supplier Management"): ofisin verisidir,
 * gemiye inmez (ticari bilgi). Kod sistemden verilir (sorular #79). Hata iletileri koddur.
 */

export const SUPPLIER_TYPES = ['Manufacturer', 'Distributor', 'Service Provider'] as const;
export type SupplierType = (typeof SUPPLIER_TYPES)[number];

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const flag = (fallback: boolean) => z.boolean(typed('INVALID_BOOLEAN')).default(fallback);

export const supplierSchema = z.object({
  name: text(200),
  supplierType: z.enum(SUPPLIER_TYPES, typed('INVALID_OPTION')),
  country: optionalText(80),
  city: optionalText(80),
  address: optionalText(400),
  contactPerson: optionalText(120),
  email: optionalText(254).pipe(
    z.union([z.null(), z.email('INVALID_EMAIL')], { error: 'INVALID_EMAIL' }),
  ),
  phone: optionalText(40),
  website: optionalText(200),
  /** Onaylı tedarikçi: yalnız onaylı ve etkin tedarikçi RFQ'ya davet edilir (sorular #79). */
  approved: flag(false),
  preferred: flag(false),
  emergency: flag(false),
  /** Tedarikçi silinmez, pasifleştirilir [NR496 4.3.2a]. */
  active: flag(true),
  paymentTerms: optionalText(200),
  currency: optionalText(3).pipe(
    z.union([z.null(), z.string().regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY')], {
      error: 'INVALID_CURRENCY',
    }),
  ),
  bankInfo: optionalText(1000),
  taxInfo: optionalText(200),
  /** Performans puanı 1–5 (teslim ve kalite; `satin-alma-modulu.md` §4). */
  rating: z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(1, 'OUT_OF_RANGE')
    .max(5, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null),
  remarks: optionalText(2000),
  /**
   * Hizmet bölgeleri (ADR 0113 §3): ofisin "Publish to Portal" penceresindeki bölge süzgeci.
   * Portal kartında tedarikçinin kendi seçtiği bölgelerle açılır; ofis düzeltebilir.
   */
  serviceRegions: z
    .array(z.enum(SERVICE_REGIONS, typed('INVALID_OPTION')), typed('INVALID_OPTION'))
    .max(SERVICE_REGIONS.length, 'TOO_MANY')
    .transform((value) => [...new Set(value)])
    .optional(),
});
export type Supplier = z.output<typeof supplierSchema>;

export function parseSupplier(input: unknown): ParseResult<Supplier> {
  const result = supplierSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Sıradaki tedarikçi kodu (sorular #79): `SUP-NNNN`, şirket içinde sıralı. */
export function nextSupplierCode(existing: readonly string[]): string {
  let last = 0;
  for (const code of existing) {
    const match = /^SUP-(\d+)$/.exec(code);
    if (match) last = Math.max(last, Number(match[1]));
  }
  return `SUP-${String(last + 1).padStart(4, '0')}`;
}

/** RFQ'ya davet edilebilir mi (sorular #79): onaylı ve etkin. */
export function supplierInvitable(supplier: { approved: boolean; active: boolean }): boolean {
  return supplier.approved && supplier.active;
}
